import React, { useCallback, useContext, useState } from "react";
import { useQuery } from "react-query";
import { cleanObj } from "../../utils";
import useStudentsQueries from "../student/hooks/useStudentQueries";
import { parseToBillingFrontEndModel } from "./helpers/parseToBillingFrontEndModel";
import {
  IBillingFilter,
  IBillingPageParams,
  IGetBillingReceiptFrontendModel,
} from "./types";

export const BillingContext = React.createContext({} as any);

export const BillingPageProvider: React.FC<
  { children: React.ReactNode } & IBillingPageParams
> = ({ children, ...billingPageParams }) => {
  const provider = useBillingPageProvider(billingPageParams);

  return (
    <BillingContext.Provider value={provider}>
      {children}
    </BillingContext.Provider>
  );
};

export const useBillingPage = function () {
  return useContext(BillingContext);
};

export const useBillingPageProvider = function ({
  showFilter,
  schoolId,
  agentId,
  parentId,
  studentId,
}: IBillingPageParams) {
  const {
    fetchBillingStudentNames,
    fetchBillingStudentCountries,
    fetchBillingReceipts,
  } = useStudentsQueries();

  const [filter, setFilter] = useState<IBillingFilter>();

  const fetchReceipts: () => Promise<IGetBillingReceiptFrontendModel[]> = () =>
    fetchBillingReceipts(
      cleanObj({
        schoolId,
        agentId,
        parentId,
        studentId,
        isForStudent: !showFilter,
        ...(filter?.student && {
          studentId: filter.student.id,
        }),
        ...(filter?.countries && {
          countries: filter.countries
            .map((country) => country.code)
            .sort()
            .join(),
        }),
      }),
    ).then((response) => {
      return parseToBillingFrontEndModel(response.data);
    });

  const {
    data: { data: studentNames = [] } = {},
    isLoading: isLoadingStudentNames,
    isFetching: isFetchingStudentNames,
  } = useQuery<any>(
    `billingStudentNames${schoolId || agentId || parentId}`,
    () => fetchBillingStudentNames(cleanObj({ schoolId, agentId, parentId })),
    {
      enabled: showFilter,
    },
  );

  const {
    data: { data: studentCountries = [] } = {},
    isLoading: isLoadingStudentCountries,
    isFetching: isFetchingStudentCountries,
  } = useQuery<any>(
    `billingStudentCountries${schoolId || agentId || parentId}`,
    () => fetchBillingStudentCountries(cleanObj({ schoolId, agentId, parentId })),
    {
      enabled: showFilter,
    },
  );

  const {
    data: billingReceipts = [],
    isLoading,
    isFetching,
  } = useQuery<IGetBillingReceiptFrontendModel[]>(
    `billingReceipts${studentId}${schoolId}${agentId}${parentId}${JSON.stringify(filter)}`,
    fetchReceipts,
  );

  const updateFilter = useCallback(
    (data) => {
      setFilter(data);
    },
    [setFilter],
  );

  return {
    updateFilter,
    studentNames,
    isLoadingNames: isLoadingStudentNames || isFetchingStudentNames,
    studentCountries,
    isLoadingCountries: isLoadingStudentCountries || isFetchingStudentCountries,
    billingReceipts,
    isLoading: isLoading || isFetching,
  };
};
